import React, { useState, useEffect } from 'react';
import { X, Mail, Plus, Trash2, AlertTriangle, CheckCircle, Edit2 } from 'lucide-react';
import { useTranslation } from '../../../TranslationContext';
import { 
  fetchNotificationEmails, 
  addNotificationEmail, 
  removeNotificationEmail,
  updateNotificationEmail,
  isValidEmail
} from './../../api/ApiNotifications';

interface NotificationListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Définition de l'interface pour un email
interface EmailItem {
  id: string;
  email: string;
}

interface NotificationMessage {
  type: 'error' | 'success';
  text: string;
}

const COLORS = {
  primary: '#c06a70',
  secondary: '#879bae',
  background: '#f5f5dc',
  darkText: '#333333',
  lightText: '#ffffff',
  border: '#e2e8f0',
  danger: '#f44336',
  success: '#4caf50',
};

const NotificationListModal: React.FC<NotificationListModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<EmailItem[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<NotificationMessage | null>(null);
  const [isModalMounted, setIsModalMounted] = useState(false);
  const [editingEmail, setEditingEmail] = useState<EmailItem | null>(null);
  const [editEmailValue, setEditEmailValue] = useState('');

  const companyId = localStorage.getItem('company') || '';

  // Contrôle de montage/démontage du modal
  useEffect(() => {
    if (isOpen) {
      setIsModalMounted(true);
    }
  }, [isOpen]);

  // Fetch existing notification emails when modal opens
  useEffect(() => {
    if (isOpen && isModalMounted) {
      loadNotificationEmails();
    }
  }, [isOpen, isModalMounted]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadNotificationEmails = async () => {
    setIsLoading(true);
    try {
      const emailList = await fetchNotificationEmails(companyId);
      setEmails(emailList || []);
      setNotification(null);
    } catch (err) {
      console.error("Erreur lors du chargement des emails:", err);
      setNotification({
        type: 'error',
        text: t('failed_to_load_emails')
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add new email
  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      setNotification({
        type: 'error',
        text: t('email_required')
      });
      return;
    }

    if (!isValidEmail(newEmail)) {
      setNotification({
        type: 'error',
        text: t('invalid_email_format')
      });
      return;
    }

    if (emails.some(item => item.email === newEmail)) {
      setNotification({
        type: 'error',
        text: t('email_already_exists')
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await addNotificationEmail(companyId, newEmail);
      const newId = result?.id || `temp-${Date.now()}`;
      
      setEmails(prevEmails => [...prevEmails, { id: newId, email: newEmail }]);
      setNewEmail('');
      
      setNotification({
        type: 'success',
        text: t('email_added_successfully')
      });
    } catch (err) {
      console.error("Erreur lors de l'ajout de l'email:", err);
      setNotification({
        type: 'error',
        text: t('failed_to_add_email')
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit email
  const handleEditEmail = async (emailItem: EmailItem) => {
    if (editingEmail?.id === emailItem.id) {
      // Validation de l'email modifié
      if (!editEmailValue.trim()) {
        setNotification({
          type: 'error',
          text: t('email_required')
        });
        return;
      }

      if (!isValidEmail(editEmailValue)) {
        setNotification({
          type: 'error',
          text: t('invalid_email_format')
        });
        return;
      }

      if (emails.some(item => item.email === editEmailValue && item.id !== emailItem.id)) {
        setNotification({
          type: 'error',
          text: t('email_already_exists')
        });
        return;
      }

      setIsLoading(true);
      try {
        await updateNotificationEmail(companyId, emailItem.id, editEmailValue);
        setEmails(prevEmails =>
          prevEmails.map(item =>
            item.id === emailItem.id ? { ...item, email: editEmailValue } : item
          )
        );
        setEditingEmail(null);
        setEditEmailValue('');
        setNotification({
          type: 'success',
          text: t('email_updated_successfully')
        });
      } catch (err) {
        console.error("Erreur lors de la modification de l'email:", err);
        setNotification({
          type: 'error',
          text: t('failed_to_update_email')
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setEditingEmail(emailItem);
      setEditEmailValue(emailItem.email);
    }
  };

  // Remove email
  const handleRemoveEmail = async (emailToRemove: EmailItem) => {
    setIsLoading(true);
    try {
      await removeNotificationEmail(companyId, emailToRemove.id);
      setEmails(prevEmails => prevEmails.filter(item => item.id !== emailToRemove.id));
      
      setNotification({
        type: 'success',
        text: t('email_removed_successfully')
      });
    } catch (err) {
      console.error("Erreur lors de la suppression de l'email:", err);
      setNotification({
        type: 'error',
        text: t('failed_to_remove_email')
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press (Enter to add email)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddEmail();
    }
  };

  if (!isOpen) {
    if (isModalMounted) {
      setTimeout(() => setIsModalMounted(false), 200);
    }
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 100,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md relative"
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Modal Header */}
        <div 
          className="p-6 pb-0 flex justify-between items-center"
          style={{ color: COLORS.darkText }}
        >
          <h2 className="text-xl font-bold flex items-center">
            <Mail className="mr-2" size={24} style={{ color: COLORS.primary }} />
            {t('notification_email_list')}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={t('close')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Notification Message */}
        {notification && (
          <div 
            className="mx-6 mt-4 p-3 rounded-lg flex items-center"
            style={{ 
              backgroundColor: notification.type === 'error' 
                ? `${COLORS.danger}10` 
                : `${COLORS.success}10`,
              color: notification.type === 'error' 
                ? COLORS.danger 
                : COLORS.success,
              transition: 'all 0.3s ease'
            }}
          >
            {notification.type === 'error' ? (
              <AlertTriangle className="mr-2" size={20} />
            ) : (
              <CheckCircle className="mr-2" size={20} />
            )}
            <p className="text-sm">{notification.text}</p>
          </div>
        )}

        {/* Email Input */}
        <div className="p-6 pt-4">
          <div className="flex mb-4">
            <input 
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (notification?.type === 'error') {
                  setNotification(null);
                }
              }}
              onKeyPress={handleKeyPress}
              placeholder={t('enter_email_address')}
              className="flex-grow p-2.5 border rounded-l-lg focus:outline-none focus:ring-2"
              style={{ 
                borderColor: COLORS.border
              }}
              disabled={isLoading}
            />
            <button
              onClick={handleAddEmail}
              disabled={isLoading}
              className="px-4 py-2.5 bg-opacity-90 text-white rounded-r-lg flex items-center justify-center transition-colors disabled:opacity-50 hover:bg-opacity-100"
              style={{ backgroundColor: COLORS.primary }}
            >
              {isLoading ? (
                <div 
                  className="w-5 h-5 border-2 rounded-full animate-spin" 
                  style={{ 
                    borderColor: 'rgba(255,255,255,0.3)', 
                    borderTopColor: 'white' 
                  }}
                />
              ) : (
                <Plus size={20} />
              )}
            </button>
          </div>

          {/* Email List */}
          <div 
            className="border rounded-lg max-h-64 overflow-y-auto"
            style={{ borderColor: COLORS.border }}
          >
            {isLoading && emails.length === 0 ? (
              <div className="p-4 text-center" style={{ color: COLORS.secondary }}>
                {t('loading_emails')}
              </div>
            ) : emails.length === 0 ? (
              <div className="p-4 text-center" style={{ color: COLORS.secondary }}>
                {t('no_emails_added')}
              </div>
            ) : (
              <ul>
                {emails.map((item) => (
                  <li
                    key={item.id}
                    className="flex justify-between items-center p-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
                    style={{ 
                      borderColor: COLORS.border,
                      color: COLORS.darkText 
                    }}
                  >
                    {editingEmail?.id === item.id ? (
                      <input
                        type="email"
                        value={editEmailValue}
                        onChange={(e) => setEditEmailValue(e.target.value)}
                        className="flex-grow p-2 border rounded mr-2"
                        style={{ borderColor: COLORS.border }}
                        disabled={isLoading}
                      />
                    ) : (
                      <span className="flex-grow mr-4 truncate">{item.email}</span>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditEmail(item)}
                        disabled={isLoading}
                        className="text-blue-500 hover:text-blue-700 disabled:opacity-50 transition-colors"
                        aria-label={t(editingEmail?.id === item.id ? 'save_email' : 'edit_email')}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleRemoveEmail(item)}
                        disabled={isLoading}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                        aria-label={t('remove_email')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationListModal;
